import { useId } from "react";

import { FormControl, FormLabel, useTheme } from "@mui/material";

import { FormRadioSelect } from "../FormRadioSelect";
import { FormTypography } from "../FormTypography";

import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export interface FormRadioSelectGroupOption<T = string | number | boolean> {
  label: string;
  value: T;
}

interface FormRadioSelectGroupProps<T = string | number | boolean> {
  name?: string;
  options: FormRadioSelectGroupOption<T>[];
  value?: T;
  onChange?: (value: T) => void;
  size?: "small" | "medium";
  layout?: "horizontal" | "vertical";
  label?: string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  sx?: object;
  "aria-labelledby"?: string;
}

export const FormRadioSelectGroup = <T extends string | number | boolean = boolean>({
  name,
  options,
  value,
  onChange,
  size = "small",
  layout = "horizontal",
  label,
  required = false,
  errorMessage,
  disabled = false,
  sx,
  "aria-labelledby": ariaLabelledBy,
}: FormRadioSelectGroupProps<T>) => {
  const theme = useTheme();
  const errorId = useId();

  if (!theme.palette.forms || !theme.palette.formsComponents?.radioSelect) {
    return <InvalidThemeErrorMessage />;
  }

  const containerStyles = {
    display: "flex",
    flexDirection: "column" as const,
    gap: theme.spacing(0.5),
    ...sx,
  };

  const radioGroupStyles = {
    display: "flex",
    flexDirection: layout === "horizontal" ? ("row" as const) : ("column" as const),
    gap: layout === "horizontal" ? theme.spacing(2) : theme.spacing(1),
  };

  return (
    <div style={containerStyles}>
      <FormControl component="fieldset" error={Boolean(errorMessage)} disabled={disabled}>
        {label && (
          <FormLabel component="legend" required={required}>
            <FormTypography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.formsComponents.radioSelect.textColor,
              }}
            >
              {label}
            </FormTypography>
          </FormLabel>
        )}
        <div
          role="radiogroup"
          aria-labelledby={label ? undefined : ariaLabelledBy}
          aria-describedby={errorMessage ? errorId : undefined}
          style={radioGroupStyles}
        >
          {options.map((option, index) => (
            <FormRadioSelect
              key={index}
              name={name}
              label={option.label}
              size={size}
              checked={value === option.value}
              onChange={() => onChange?.(option.value)}
              disabled={disabled}
            />
          ))}
        </div>
      </FormControl>
      {errorMessage && (
        <FormTypography
          id={errorId}
          role="alert"
          vibe="error"
          variant="caption"
          style={{ fontSize: "12px", lineHeight: "16px", fontWeight: 400 }}
        >
          {errorMessage}
        </FormTypography>
      )}
    </div>
  );
};
